<template>
  <div
    class="flex flex-col p-0 rounded-2xl border-solid border border-slate-200 transition-all duration-150 cursor-text"
    :class="[$dm('bg-white', 'dark:bg-slate-600 dark:border-slate-600')]"
    :style="inputWrapperStyle"
    @keydown.esc="hideEmojiPicker"
    @click="handleWrapperClick"
  >
    <CsCollapse
      :open-duration="0.3"
      :open-bounce="0.1"
      :close-duration="0.3"
      :close-bounce="0.1"
      :is-open="hasReplyTo"
    >
      <div class="px-2 pt-2">
        <div
          class="bg-brand-gray-6 rounded-lg w-full flex items-stretch overflow-hidden"
        >
          <span class="w-1 flex-shrink-0" :style="replyIndicatorStyle" />
          <div class="flex-1 flex min-w-0 py-1.5 pl-2 pr-1">
            <div class="flex flex-col w-full min-w-0">
              <CsText
                variant="body-sm"
                variant-color="secondary"
                weight="semibold"
                class="truncate"
              >
                {{ replyToSenderName }}
              </CsText>
              <CsText
                variant="body-sm"
                variant-color="secondary"
                class="truncate"
              >
                {{ replyToContent }}
              </CsText>
            </div>

            <div class="flex-none">
              <CsButton
                is-icon-only
                size="sm"
                variant="tertiary"
                @click="$emit('click-dismiss-reply-to')"
              >
                <CsIcon name="x" class="h-4 w-4" />
              </CsButton>
            </div>
          </div>
        </div>
      </div>
    </CsCollapse>

    <!-- Textarea area -->
    <div class="w-full pr-2 pl-3 pt-2">
      <textarea
        id="chat-input"
        ref="chatInput"
        v-model="userInput"
        :rows="1"
        :aria-label="$t('CHAT_PLACEHOLDER')"
        :placeholder="$t('CHAT_PLACEHOLDER')"
        :class="[$dm('text-black-900', 'dark:text-slate-50')]"
        class="w-full border-0 outline-none resize-none h-5 min-h-[20px] max-h-[120px] -mb-1.5 text-sm bg-transparent placeholder:text-brand-text-secondary placeholder:!text-sm"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keyup="onKeyup"
      />
    </div>

    <!-- Bottom toolbar -->
    <div class="flex items-center justify-between pb-2 px-2 pt-0.5">
      <div class="flex items-center gap-1">
        <CsButton
          v-if="hasAttachmentsEnabled"
          variant="tertiary"
          is-icon-only
          @click="triggerAttachment"
        >
          <CsIcon name="attachment" class="h-5 w-5" />
        </CsButton>
        <input
          ref="fileInput"
          type="file"
          class="hidden"
          @change="onFileChange"
        />

        <CsButton
          v-if="hasEmojiPickerEnabled"
          :class="[showEmojiPicker ? '!text-woot-500' : '']"
          variant="tertiary"
          is-icon-only
          :data-pressed="showEmojiPicker"
          @click="toggleEmojiPicker"
        >
          <CsIcon name="smiley-happy" class="h-5 w-5" />
        </CsButton>

        <emoji-input
          v-if="showEmojiPicker"
          v-on-clickaway="hideEmojiPicker"
          :on-click="emojiOnClick"
          @keydown.esc="hideEmojiPicker"
        />
      </div>

      <!-- Send button - always visible -->
      <button
        type="button"
        class="cs-widget-chat-input__send-btn flex items-center justify-center w-8 h-8 rounded-full border-none cursor-pointer transition-all duration-150"
        :class="{
          'is-enabled hover:opacity-90': hasContent,
          'is-disabled bg-brand-gray-6 text-brand-gray-9 !cursor-not-allowed dark:bg-slate-700 dark:text-slate-500':
            !hasContent,
        }"
        :style="sendButtonStyle"
        :disabled="!hasContent"
        @click="handleButtonClick"
      >
        <CsIcon name="send" class="h-5 w-5" />
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';

import configMixin from 'widget/mixins/configMixin';
import darkModeMixin from 'widget/mixins/darkModeMixin.js';
import {
  CsIcon,
  CsButton,
  CsText,
  CsCollapse,
} from 'dashboard/components/ui/commslayer';
import { MESSAGE_TYPE } from 'widget/helpers/constants';

const EmojiInput = () => import('shared/components/emoji/EmojiInput');

const TYPING_INDICATOR_IDLE_TIME = 4000;

export default {
  name: 'CsWidgetChatEditor',
  components: {
    CsIcon,
    CsButton,
    CsText,
    CsCollapse,
    EmojiInput,
  },
  mixins: [clickaway, configMixin, darkModeMixin],
  props: {
    onSendMessage: {
      type: Function,
      default: () => {},
    },
    onSendAttachment: {
      type: Function,
      default: () => {},
    },
    inReplyTo: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      userInput: '',
      showEmojiPicker: false,
      isFocused: false,
      idleTimer: null,
    };
  },

  computed: {
    ...mapGetters({
      widgetColor: 'appConfig/getWidgetColor',
      isWidgetOpen: 'appConfig/getIsWidgetOpen',
    }),
    hasContent() {
      return this.userInput.trim().length > 0;
    },
    inputWrapperStyle() {
      const hexToRgba = (hex, alpha) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };

      if (!this.isFocused) {
        return {};
      }

      const color = this.widgetColor;
      return {
        borderColor: color,
        boxShadow: `0 0 0 3px ${hexToRgba(color, 0.15)}`,
      };
    },
    sendButtonStyle() {
      if (!this.hasContent) {
        return {};
      }
      return {
        backgroundColor: this.widgetColor,
        color: '#ffffff',
      };
    },
    replyIndicatorStyle() {
      const darkenHex = (hex, percent) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        const factor = 1 - percent / 100;
        const newR = Math.round(r * factor);
        const newG = Math.round(g * factor);
        const newB = Math.round(b * factor);
        return `rgb(${newR}, ${newG}, ${newB})`;
      };

      return {
        backgroundColor: darkenHex(this.widgetColor, 10),
      };
    },
    hasReplyTo() {
      return Boolean(
        this.inReplyTo && (this.inReplyTo.content || this.inReplyTo.attachments)
      );
    },
    replyToSenderName() {
      if (!this.inReplyTo) return '';
      // INCOMING = customer's own message (from widget user to business)
      if (this.inReplyTo.message_type === MESSAGE_TYPE.INCOMING) {
        return this.$t('YOU');
      }
      // OUTGOING/TEMPLATE = agent/bot message (from business to customer)
      const sender = this.inReplyTo.sender;
      return (
        sender?.available_name || sender?.name || this.$t('UNREAD_VIEW.BOT')
      );
    },
    replyToContent() {
      if (!this.inReplyTo) return '';
      if (this.inReplyTo.content) {
        return this.inReplyTo.content;
      }
      // Fallback to attachment type
      if (this.inReplyTo.attachments?.length) {
        const [{ file_type: fileType } = {}] = this.inReplyTo.attachments;
        return this.$t(`ATTACHMENTS.${fileType}.CONTENT`);
      }
      return '';
    },
  },
  watch: {
    isWidgetOpen(isWidgetOpen) {
      if (isWidgetOpen) {
        this.focusInput();
      }
    },
    userInput() {
      this.$nextTick(() => {
        this.resizeTextarea();
      });
    },
  },
  destroyed() {
    document.removeEventListener('keypress', this.handleEnterKeyPress);
    this.turnOffIdleTimer();
  },
  mounted() {
    document.addEventListener('keypress', this.handleEnterKeyPress);
    if (this.isWidgetOpen) {
      this.focusInput();
    }
  },

  methods: {
    resizeTextarea() {
      const textarea = this.$refs.chatInput;
      if (!textarea) return;

      textarea.style.height = 'auto';
      if (!this.userInput) {
        textarea.style.height = '20px';
      } else {
        const maxHeight = 120;
        const newHeight = Math.min(textarea.scrollHeight, maxHeight);
        textarea.style.height = `${newHeight}px`;
      }
    },
    onInput() {
      this.resizeTextarea();
    },
    onBlur() {
      this.isFocused = false;
      this.turnOffIdleTimer();
      this.resetTyping();
    },
    onFocus() {
      this.isFocused = true;
    },
    onKeyup() {
      if (!this.idleTimer) {
        this.toggleTyping('on');
      }
      this.turnOffIdleTimer();
      this.idleTimer = setTimeout(
        () => this.resetTyping(),
        TYPING_INDICATOR_IDLE_TIME
      );
    },
    resetTyping() {
      this.toggleTyping('off');
      this.idleTimer = null;
    },
    turnOffIdleTimer() {
      if (this.idleTimer) {
        clearTimeout(this.idleTimer);
      }
    },
    handleButtonClick() {
      if (this.userInput && this.userInput.trim()) {
        this.onSendMessage(this.userInput);
      }
      this.userInput = '';
      this.$nextTick(() => {
        this.resizeTextarea();
        this.focusInput();
      });
    },
    handleEnterKeyPress(e) {
      if (e.keyCode === 13 && !e.shiftKey) {
        e.preventDefault();
        this.handleButtonClick();
      }
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    hideEmojiPicker(e) {
      if (this.showEmojiPicker) {
        e.stopPropagation();
        this.toggleEmojiPicker();
      }
    },
    emojiOnClick(emoji) {
      this.userInput = `${this.userInput}${emoji} `;
    },
    toggleTyping(typingStatus) {
      this.$store.dispatch('conversation/toggleUserTyping', { typingStatus });
    },
    focusInput() {
      this.$refs.chatInput.focus();
    },
    handleWrapperClick(event) {
      // Don't focus if clicking on interactive elements
      const interactiveSelectors = [
        'button',
        'input[type="file"]',
        '.emoji-dialog',
        '[role="button"]',
      ];
      const isInteractive = interactiveSelectors.some(selector =>
        event.target.closest(selector)
      );
      if (!isInteractive) {
        this.focusInput();
      }
    },
    triggerAttachment() {
      this.$refs.fileInput.click();
    },
    getFileType(mimeType) {
      return mimeType.includes('image') ? 'image' : 'file';
    },
    getLocalFileAttributes(file) {
      return {
        thumbUrl: window.URL.createObjectURL(file),
        fileType: this.getFileType(file.type),
      };
    },
    onFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        // Create attachment object with required properties for store action
        const attachment = {
          file,
          ...this.getLocalFileAttributes(file),
        };
        this.onSendAttachment(attachment);
        // Reset input
        event.target.value = '';
      }
    },
  },
};
</script>

<style scoped lang="scss">
// Send button enabled state - color set via inline style
.cs-widget-chat-input__send-btn.is-enabled {
  color: #ffffff;
}

// Emoji dialog positioning - can't be done in Tailwind due to :deep
:deep(.emoji-dialog) {
  right: 20px;
  bottom: 60px;
  top: auto;
  max-width: 100%;

  &::before {
    display: none;
  }
}
</style>
